"use client"

import type { MembersBoardToConfirm, MembersBoardWaitingPerson } from "@ibada/shared-types/overview"
import { CheckIcon } from "lucide-react"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { BoardPersonRow } from "@/features/members/components/board/board-person-row"
import { useLocale, useTranslations } from "@/i18n"

const MAX_ROWS = 4

export type QuickConfirm = {
  onConfirm: (person: MembersBoardWaitingPerson) => void
  /** The record being confirmed right now, if any. */
  confirmingId: string | null
}

/**
 * To confirm: records waiting for confirmation, the longest waiting first, each with a one-click confirm for a
 * reader who may edit members (the server still decides). Shown when empty too ("nobody waiting"): a tile that
 * vanished with its data would shuffle every tile after it.
 */
export function ToConfirmTile({
  toConfirm,
  pendingLink,
  profileHref,
  confirm,
}: {
  toConfirm: MembersBoardToConfirm
  pendingLink: HomeTileLink | null
  profileHref: ((memberId: string) => string) | null
  confirm: QuickConfirm | null
}) {
  const t = useTranslations("people.members.overview.board.toConfirm")
  const locale = useLocale()
  const dateFormat = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" })

  return (
    <HomeTile
      title={t("title")}
      subtitle={
        toConfirm.total === 0
          ? t("subtitleNone")
          : t(toConfirm.total === 1 ? "subtitleOne" : "subtitleOther", { count: toConfirm.total })
      }
      link={pendingLink && toConfirm.total > 0 ? { ...pendingLink, label: t("all", { count: toConfirm.total }) } : null}
      testId='members-board-to-confirm'
    >
      {toConfirm.items.length === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <ul className='flex flex-col gap-2.5'>
          {toConfirm.items.slice(0, MAX_ROWS).map((person) => (
            <BoardPersonRow
              key={person.id}
              testId='to-confirm-row'
              person={person}
              profileHref={profileHref ? profileHref(person.id) : null}
              detail={t("waitingSince", { date: dateFormat.format(new Date(person.createdAt)) })}
              showCall={false}
              action={
                confirm ? (
                  <button
                    type='button'
                    onClick={() => confirm.onConfirm(person)}
                    disabled={confirm.confirmingId !== null}
                    aria-label={t("confirmName", { name: person.firstName })}
                    className='inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-2.5 text-[12px] font-bold text-white hover:bg-emerald-700 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-60'
                  >
                    <CheckIcon aria-hidden className='size-3.5' />
                    {confirm.confirmingId === person.id ? t("confirming") : t("confirm")}
                  </button>
                ) : null
              }
            />
          ))}
        </ul>
      )}
    </HomeTile>
  )
}
